import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useMemo } from 'react';
import { TmdxSwitch } from '@ds';
import { FormRow, FormSection } from '@features/components/CaseFormBlocks/components';
import { useDuplicateCaseWarning } from '@features/components/CaseFormBlocks/hooks/useDuplicateCaseWarning';
import { CaseDonor, CaseGeneral, CaseTxCenter } from '@lp/features/CaseFormSections';
import { getAvailableOrganTypes } from '@lp/features/CaseFormSections/utils';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import {
  CaseAttachments,
  OpoRequestCheckbox,
  Reallocation,
  ReallocationCheckbox,
} from '@lp/features/CaseFormSections/components';
import { Uuid } from '@/api/types';
import { CaseStatus, OrganType } from '@/api/apis/case';

export const CreateCaseForm = ({
  hasPermission,
  reAllocationCaseId,
  caseId,
  caseStatus,
  isEditDraft = false,
  canCreateKidneyCase = true,
  originalOrganType,
}: {
  hasPermission: boolean;
  reAllocationCaseId?: Uuid | null;
  caseId?: string;
  caseStatus?: CaseStatus;
  isEditDraft?: boolean;
  canCreateKidneyCase?: boolean;
  originalOrganType?: OrganType;
}) => {
  const { control } = useFormContext<CaseFormType>();
  const [reAllocationCaseTrialProgram, originalReAllocationCaseId, currentOrganType] = useWatch({
    control,
    name: ['reAllocationCaseTrialProgram', 'originalReAllocationCaseId', 'organType'],
  });

  const availableOrganTypes = useMemo<OrganType[]>(
    () => getAvailableOrganTypes({ canCreateKidneyCase, isEditDraft, originalOrganType, currentOrganType }),
    [canCreateKidneyCase, isEditDraft, originalOrganType, currentOrganType],
  );

  const { isDuplicate } = useDuplicateCaseWarning({ excludeCaseId: caseId });
  const isReallocationDisabled =
    !hasPermission || (isEditDraft && !!originalReAllocationCaseId && !!reAllocationCaseTrialProgram);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-[100%]">
        <OpoRequestCheckbox hasPermission={hasPermission} reAllocationCaseId={reAllocationCaseId} />
        <ReallocationCheckbox isDisabled={isReallocationDisabled} />
      </div>
      <Reallocation initialReallocationCaseId={reAllocationCaseId} disabled={isReallocationDisabled} override />
      <FormSection title="Case General Info">
        <CaseGeneral
          showRequired
          hasPermission={hasPermission}
          isDuplicate={isDuplicate}
          allowDirectAcquisition={hasPermission}
          caseStatus={caseStatus}
          availableOrganTypes={availableOrganTypes}
        />
      </FormSection>
      <FormSection title="TX Center Info">
        {/**

         temporarily hidden in scope of - TMDXLP-6792
         {isExpeditedCase && (
            <InfoBanner iconType="info">
              <span className="text-m-sb">Expedited case:</span> The TX Center can be selected only after the team is
              assigned.
            </InfoBanner>
          )}
          <CaseTxCenter
            hasPermission={hasPermission}
            disabled={{ txCenterId: isExpeditedCase, txCenterAddress: isExpeditedCase }}
          />

        */}
        <CaseTxCenter hasPermission={hasPermission} />
      </FormSection>
      <FormSection title="Retrieval Info">
        <CaseDonor hasPermission={hasPermission} />
        <CaseAttachments disabled={!hasPermission} />
      </FormSection>
      <FormRow title="Under 40kg">
        <Controller
          name="pediatricCase"
          control={control}
          render={({ field: { onChange, value } }) => (
            <div className="flex flex-row gap-2">
              <span className="text-l-m text-base-5">No</span>
              <TmdxSwitch
                color="default"
                size="sm"
                isSelected={value}
                isDisabled={!hasPermission}
                onValueChange={onChange}
              >
                <span className="text-l-m text-base-5">Yes</span>
              </TmdxSwitch>
            </div>
          )}
        />
      </FormRow>
    </div>
  );
};
